import React from 'react';
import styled from 'styled-components/macro';
import { OrganizationType } from 'store/modules/auth/auth';
import { SummaryWrapper, PaymentsBalance } from '@deeptrue-frontend/ui';
import IntlProvider, {
  useFormatMessage,
  useCurrency,
} from 'components/IntlProvider';

import { usePaymentsLimits } from 'hooks';

const Wrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PaymentWrapper = styled.div`
  margin-top: 1rem;
`;

interface StatusType {
  value: number;
  display: string;
}

interface Props {
  model?: any;
  organization: OrganizationType;
  goToPayments: () => void;
  withPayments?: boolean;
  hasSufficientCredits?: boolean;
  hasDataCollectionStarted: boolean;
  applyVoucher: (
    code: string,
    callbackSuccess: () => void,
    callbackFailure: () => void
  ) => void;
  removeVoucher: (callback: () => void, callbackFailure: () => void) => void;
}

export const Sidebar = ({
  model,
  organization,
  goToPayments,
  withPayments = true,
  hasSufficientCredits,
  hasDataCollectionStarted,
  applyVoucher,
  removeVoucher,
}: Props) => {
  const t = useFormatMessage();
  const { available_tokens } = usePaymentsLimits({ organization }).limitsRes;
  const { arePaymentInformationLoaded } = usePaymentsLimits({
    organization,
  });
  const formatCurrency = useCurrency();
  const { locale } = IntlProvider.useIntl();

  return (
    <Wrapper>
      {withPayments && (
        <SummaryWrapper
          model={model}
          locale={locale}
          i18n={{
            title: t('briefing.summary.totalCost', 'Total cost'),
            withoutVat: t('briefing.summary.withoutVat', 'excl. VAT'),
            credits: t('briefing.summary.credits', 'Credits'),
            baseFee: t('briefing.summary.baseFee', 'Base fee'),
            dataFee: t('briefing.summary.dataFee', 'Data fee'),
            brands: t('briefing.summary.brands', 'No. of brands'),
            pricePerResponse: t(
              'briefing.summary.pricePerResponse',
              'Price per Response'
            ),
            numberOfCustomers: t(
              'briefing.summary.customersNr',
              'Number of customers'
            ),
            numberOfCustomersSum: t(
              'briefing.summary.customersNrSum',
              'Total number of respondents'
            ),
            country: t('briefing.summary.country', 'Countries'),
            source: {
              title: t('source.title', 'Respondents source'),
              contacts: t('source.contacts', 'own contacts'),
              deeptrue: t('source.deeptrue', 'by deeptrue'),
              both: t('source.both', 'both'),
            },
            type: {
              title: t('type.title', 'Type of respondents'),
              typeOne: t('type.typeOne', 'Users'),
              typeTwo: t('type.typeTwo', 'Users & Non-Users'),
            },
            gender: {
              title: t('gender.title', 'Gender'),
              male: t('gender.male', 'Male'),
              female: t('gender.female', 'Female'),
              both: t('gender.botj', 'male & female'),
              other: t('gender.other', 'Other'),
            },
            age: t('briefing.summary.age', 'Age'),
            year: t('briefing.summary.year', 'years'),
            insights: t('briefing.summary.insights', 'Insights included'),
            attributes: t(
              'briefing.summary.attributes',
              'Attribute association'
            ),
            behavior: t('briefing.summary.behavior', 'Behavior Change'),
            innovation: t(
              'briefing.summary.innovation',
              'Degree of innovation'
            ),
            advertisingMessage: t(
              'briefing.summary.advertisingMessage',
              'Transfer of your intended message'
            ),
            unique: t('briefing.summary.unique', 'Uniqueness'),
            relevance: t('briefing.summary.relevance', 'Relevance'),
            credibility: t('briefing.summary.credibility', 'Credibility'),
            cultural: t('briefing.summary.cultural', 'Potential offensiveness'),
            brand: t('briefing.summary.brand', 'Brand Fit'),
            brandImpact: t('briefing.summary.brandImpact', 'Brand Impact'),
            recall: t('briefing.summary.recall', 'Advertisement Recall'),
            detailed: t(
              'briefing.summary.detailed',
              'Detailed customer satisfaction results'
            ),
            likes: t('briefing.summary.likes', 'Likes and dislikes'),
            benchmark: t('briefing.summary.benchmark', 'Industry benchmark'),
            individual: t(
              'briefing.summary.individual',
              'Results visible on individual level'
            ),
            basic_interviews: t(
              'briefing.summary.basic_interviews',
              '10 interviews per month included'
            ),
            aggregated: t(
              'briefing.summary.aggregated',
              'Aggregated customer satisfaction results'
            ),
            advanced_interviews: t(
              'briefing.summary.advanced_interviews',
              '30 interviews per month included'
            ),
            professional_interviews: t(
              'briefing.summary.professional_interviews',
              '100 interviews per month included'
            ),
            tooltip: {
              base_fee: t(
                'briefing.summary.tooltip.base_fee',
                'Depending on the number of countries, we charge a minimum fee.'
              ),
              data_fee: t(
                'briefing.summary.tooltip.data_fee',
                'Depending on the information included in the project setup, we charge an additional fee for data extraction.'
              ),
              credits: t(
                'briefing.summary.tooltip.credits',
                'To use the deeptrue tools, you must first purchase Credits. You can buy credits in the "Payments" section of the dashboard. Immediately after, you can enjoy all the tools!'
              ),
            },
            voucher: {
              title: t('vouchers.title', 'Do you have a voucher?'),
              code: t('vouchers.code', 'Voucher code'),
              apply: t('vouchers.apply', 'Apply'),
              remove: t('vouchers.remove', 'Remove'),
              applied: t('vouchers.applied', 'Voucher applied'),
              error: t('vouchers.error', 'Invalid code'),
            },
            audienceRelevance: t(
              'briefing.summary.audienceRelevance',
              'Relevance of audience'
            ),
            impression: t('briefing.summary.impression', 'Overall Impression'),
            willingnessParticipation: t(
              'briefing.summary.willingnessParticipation',
              'Willingness to participate again'
            ),
            willingnessRecommend: t(
              'briefing.summary.willingnessRecommend',
              'Willingness to recommend'
            ),
            openFeedback: t(
              'briefing.summary.openFeedback',
              'Any open feedback'
            ),
            motivations: t('briefing.summary.motivations', 'Motivations'),
            networking: t(
              'briefing.summary.networking',
              'Networking opportunities'
            ),
            registration: t(
              'briefing.summary.registration',
              'Registration process'
            ),
            speakers: t('briefing.summary.speakers', 'Speakers and sessions'),
            inspiration: t(
              'briefing.summary.inspiration',
              'Inspiration for topics'
            ),
            discussion: t(
              'briefing.summary.discussion',
              'Duration for discussion'
            ),
            duration: t('briefing.summary.duration', 'Duration of content'),
            awareness: t('briefing.summary.awareness', 'Awareness Sources'),
            platform: t('briefing.summary.platform', 'Platform experience'),
            techSupport: t('briefing.summary.techSupport', 'Tech Support'),
            thoughts: t('briefing.summary.thoughts', 'Thoughts on event'),
            expectations: t(
              'briefing.summary.expectations',
              'Event expectations'
            ),
            benefits: t('briefing.summary.benefits', 'Benefits for sponsoring'),
            revisit: t('briefing.summary.revisit', 'Revisit'),
            informationBefore: t(
              'briefing.summary.informationBefore',
              'Information before event'
            ),
            venue: t('briefing.summary.venue', 'Venue and equipment'),
            optimization: t(
              'briefing.summary.optimization',
              'Optimization opportunities'
            ),
            acquaintances: t(
              'briefing.summary.acquaintances',
              'Acquaintances who want to speak'
            ),
            organization: t('briefing.summary.organization', 'Organisation'),
            roles: t('briefing.summary.roles', 'Roles among employees'),
            goal: t('briefing.summary.goal', 'Goal achievement'),
            businessImpact: t(
              'briefing.summary.businessImpact',
              'Impact on business goals'
            ),
            departmentImpact: t(
              'briefing.summary.departmentImpact',
              'Impact on department'
            ),
            recommend: t(
              'briefing.summary.recommend',
              'Willingness to recommend'
            ),
            volunteeringInterest: t(
              'briefing.summary.volunteeringInterest',
              'Interest in volunteering again'
            ),
          }}
          formatCurrency={formatCurrency}
          callbackApplyVoucher={applyVoucher}
          callbackRemoveVoucher={removeVoucher}
        />
      )}
      {withPayments && (
        <PaymentWrapper>
          <PaymentsBalance
            title={t('project.testEdit.balance.title', 'Your balance')}
            creditsText={t('payments.table.credits', 'Credits')}
            tooltip={t(
              'briefing.summary.tooltip.credits',
              'To use the deeptrue tools, you must first purchase Credits. You can buy credits in the "Payments" section of the dashboard. Immediately after, you can enjoy all the tools!'
            )}
            isLoaded={arePaymentInformationLoaded}
            credits={available_tokens}
            noCredits={t(
              'project.testEdit.balance.noCredits',
              'You have no credits'
            )}
            cta={t('project.testEdit.balance.buy', 'Buy credits')}
            ctaAction={goToPayments}
            insufficientBalance={!hasSufficientCredits}
            notification={t(
              'payment.notification.insufficient.balance',
              'Insufficient balance to begin Data Collection.'
            )}
            hasDataCollectionStarted={hasDataCollectionStarted}
          />
        </PaymentWrapper>
      )}
    </Wrapper>
  );
};

export default Sidebar;
